export const SITE_DISCOVERY_CEILING = 0.45;

const DISCOVERY_TAU_MS = 14_000;
const SENTINEL_PREFIX = /^SENTINEL::\s*/i;
const NOT_FOUND = /^(no|none|not|missing|absent|without|could ?n[o']?t|cannot|can't|unable|failed|no explicit)\b/i;
const NEGATIVE_ANYWHERE = /\b(not found|no mention|none found|nothing found)\b/i;
const FOUND_NOTHING = /\b0 (reviews?|results?|pages?|mentions?)\b/i;
const MACHINE_DETAIL = /^\[|^launching\b|\(\d[\d,]* (chars?|bytes?)\)/i;

const SITE_ROW_MS = 2800;
const SITE_ROW_MIN_MS = 1200;
const SITE_PASS_MAX_MS = 45_000;

export function siteRowMs(pageCount: number): number {
  if (pageCount <= 0) return SITE_ROW_MS;
  return Math.max(SITE_ROW_MIN_MS, Math.min(SITE_ROW_MS, Math.floor(SITE_PASS_MAX_MS / pageCount)));
}

export function siteFill(elapsedMs: number, totalPages: number, revealedPages: number): number {
  if (totalPages > 0) {
    const read = Math.max(0, Math.min(revealedPages, totalPages));
    return SITE_DISCOVERY_CEILING + (1 - SITE_DISCOVERY_CEILING) * (read / totalPages);
  }
  const elapsed = Math.max(0, elapsedMs);
  return SITE_DISCOVERY_CEILING * (1 - Math.exp(-elapsed / DISCOVERY_TAU_MS));
}

export function cleanFinding(line: string): string {
  const stripped = line.replace(SENTINEL_PREFIX, '').trim();
  const sentenceEnd = stripped.search(/\.\s/);
  return sentenceEnd === -1 ? stripped : stripped.slice(0, sentenceEnd + 1);
}

export function isPositiveFinding(line: string): boolean {
  const text = cleanFinding(line);
  if (!text) return false;
  if (MACHINE_DETAIL.test(text) || FOUND_NOTHING.test(text)) return false;
  return !NOT_FOUND.test(text) && !NEGATIVE_ANYWHERE.test(text);
}

const AUDIENCE_TAU_MS = 45_000;
const AUDIENCE_CARD_TAU_MS = 12_000;
const AUDIENCE_CARD_REACH = 0.85;
const TIMER_SUFFIX = /\s*\((\d+s)\)\s*$/;

function drawnShare(drawn: number, total: number): number {
  return SITE_DISCOVERY_CEILING + (1 - SITE_DISCOVERY_CEILING) * Math.min(1, (drawn - 1) / Math.max(1, total - 1));
}

export function audienceFill(elapsedMs: number, drawn: number, total: number, sinceLastCardMs = 0): number {
  if (drawn > 0) {
    if (total <= 0 || drawn >= total) return 1;
    const from = drawnShare(drawn, total);
    const to = drawnShare(drawn + 1, total);
    const eased = 1 - Math.exp(-Math.max(0, sinceLastCardMs) / AUDIENCE_CARD_TAU_MS);
    return from + (to - from) * AUDIENCE_CARD_REACH * eased;
  }
  const elapsed = Math.max(0, elapsedMs);
  return SITE_DISCOVERY_CEILING * (1 - Math.exp(-elapsed / AUDIENCE_TAU_MS));
}

interface ProgressRow {
  text: string;
  elapsed?: string;
}

export function foldProgressLines(messages: string[]): ProgressRow[] {
  const rows: ProgressRow[] = [];
  for (const message of messages) {
    const match = message.match(TIMER_SUFFIX);
    const text = message.replace(TIMER_SUFFIX, '').trim();
    if (!text) continue;
    const row: ProgressRow = match ? { text, elapsed: match[1] } : { text };
    const last = rows[rows.length - 1];
    if (last && last.text === text) rows[rows.length - 1] = row;
    else rows.push(row);
  }
  return rows;
}
